import React, { useMemo, useState } from 'react';
import {
  Banner,
  Button,
  CandidatePerformanceFeedback,
  Checkbox,
  EmptyLayout,
  FluidButtonLayout,
  Form,
  FormLabel,
  Gutter,
  IconButton,
  Layout,
  LayoutItem,
  Paragraph,
  ReadonlyTextarea,
  Textarea,
  Well,
} from '@axiom/ui';
import type {
  AhqSubmission,
  OpportunityCandidate,
  CandidateReputationClarificationPatch,
  Opportunity,
} from '@axiom/validation';

import EmptyProjectSvg from '../../public/svg/empty-project.svg';
import NoReputationSvg from '../../public/svg/no-talent-reputation.svg';
import {
  CandidateReputationCheckboxSchema,
  CandidateReputationCommentsSchema,
} from '../../schema/candidate-reputation-form-schema';
import { SubmissionsLegacyApi } from '../../api/submissions-legacy-api';
import { SubmissionsApi } from '../../api/submissions-api';
import { OpportunityLegacyApi } from '../../api/opportunity-legacy-api';

import { OpportunitySubmissionUtil } from './opportunity-submission-util';

export interface SubmissionCandidateReputationProps {
  submission: AhqSubmission;
  candidate: OpportunityCandidate;
  opportunity: Opportunity;
  name?: string;
  readOnly?: boolean;
  candidateReputationClarificationPatchCall: CandidateReputationClarificationPatch[];
  onChange?: (values: { showCandidateReputation: boolean | null }) => void;
  onFeedbackChange?: (value: {
    candidateId: string;
    candidateReputationClarification: { submissionComments: string };
  }) => void;
}
export const OpportunitySubmissionCandidateReputation = ({
  submission,
  candidate,
  opportunity,
  readOnly,
  candidateReputationClarificationPatchCall,
  onChange,
  onFeedbackChange,
}: SubmissionCandidateReputationProps) => {
  const [editComments, setEditComments] = useState(false);

  const candidateOpportunity = useMemo(
    () =>
      submission.candidateOpportunities.find(
        cOpp => cOpp.candidate.id === candidate.id
      ),
    [submission.candidateOpportunities, candidate.id]
  );

  const reputationData = useMemo(() => {
    return candidateOpportunity ? candidateOpportunity.candidate : candidate;
  }, [candidate, candidateOpportunity]);

  const tempSubmissionComments = useMemo(
    () =>
      candidateReputationClarificationPatchCall.find(
        item => item.candidateId === candidate.id
      )?.candidateReputationClarification.submissionComments,
    [candidateReputationClarificationPatchCall, candidate.id]
  );

  const currentComments =
    tempSubmissionComments ??
    candidateOpportunity?.candidateReputationClarification
      ?.submissionComments ??
    '';

  const handleCheckboxSubmit = async (
    _changedData: unknown,
    _actions: unknown,
    values: { showCandidateReputation: boolean }
  ) => {
    if (onChange) {
      onChange({ showCandidateReputation: values.showCandidateReputation });
    } else {
      const { submissionId } = submission;
      await SubmissionsLegacyApi.updateSubmission(submissionId, {
        newSubmissionType: submission.newSubmissionType,
        candidates: [
          {
            candidateId: candidate.id,
            showCandidateReputation: values.showCandidateReputation,
          },
        ],
      });
      await Promise.all([
        SubmissionsApi.refreshSubmission(submissionId),
        OpportunityLegacyApi.refreshOpportunityCandidates(opportunity.id),
      ]);
    }
  };

  const handleCommentsSubmit = async (
    _changedData: unknown,
    _actions: unknown,
    values: { submissionComments?: string | null }
  ) => {
    const submissionComments = values.submissionComments?.trim() ?? '';
    if (onFeedbackChange) {
      onFeedbackChange({
        candidateId: candidate.id,
        candidateReputationClarification: { submissionComments },
      });
    } else {
      const { submissionId } = submission;
      await SubmissionsApi.updateSubmission(submissionId, {
        candidates: [
          {
            candidateId: candidate.id,
            candidateReputationClarification: { submissionComments },
          },
        ],
      });
      await OpportunitySubmissionUtil.refreshSubmissionAndCandidates({
        submissionId,
        opportunityId: opportunity.id,
        candidateId: candidate.id,
      });
    }
    setEditComments(false);
  };

  const commentsDisplay = currentComments ? (
    <ReadonlyTextarea
      name="READONLY_SUBMISSION_COMMENTS"
      value={currentComments}
    />
  ) : (
    <EmptyLayout
      graphic={<EmptyProjectSvg width="117px" height="117px" />}
      name="NO_SUBMISSION_COMMENTS"
    >
      No additional performance feedback
    </EmptyLayout>
  );

  return (
    <>
      <Form
        schema={CandidateReputationCheckboxSchema}
        initialValues={{
          showCandidateReputation: candidate.showCandidateReputation,
        }}
        submitOnChange={!readOnly}
        onSubmit={handleCheckboxSubmit}
      >
        {({ values }) => {
          return (
            <>
              <Gutter vertical="24px">
                <Checkbox
                  name="showCandidateReputation"
                  disabled={readOnly}
                  displayValue={
                    <div data-test="PENDO_PERF_FEEDBACK_CHECKBOX">
                      Show Performance Feedback
                    </div>
                  }
                />
              </Gutter>
              {candidate.showReputation === false ? (
                <Well background="light blue" name="optedOutDisplay">
                  <Paragraph>
                    {reputationData.calculatedDisplayName ?? 'Talent'} has
                    chosen not to display client feedback.
                  </Paragraph>
                </Well>
              ) : (
                <div>
                  {reputationData.canDisplayReputationData ? (
                    <>
                      {values.showCandidateReputation ? null : (
                        <Gutter bottom="24px">
                          <Banner
                            type="info"
                            impact="high"
                            name="NO_SHOW_TO_CLIENT"
                          >
                            This performance feedback will be hidden from
                            clients.
                          </Banner>
                        </Gutter>
                      )}
                      <CandidatePerformanceFeedback
                        stacked
                        candidateReputation={reputationData.candidateReputation}
                      />
                    </>
                  ) : (
                    <Well background="light blue" name="limitedFeedbackDisplay">
                      <Layout horizontalGutter="16px" position="middle">
                        <LayoutItem>
                          <NoReputationSvg width="98" height="93" />
                        </LayoutItem>
                        <Paragraph>
                          Although client feedback is not yet available for this
                          talent, we are confident they are a great match for
                          your legal needs.
                        </Paragraph>
                      </Layout>
                    </Well>
                  )}
                </div>
              )}
            </>
          );
        }}
      </Form>
      <Gutter bottom="16px" />
      <Layout position="middle space-between">
        {!editComments && (
          <FormLabel>Add additional performance feedback</FormLabel>
        )}
        {!editComments && !readOnly && (
          <IconButton
            icon="pencil"
            pattern="secondary"
            variation="minimal"
            onClick={() => {
              setEditComments(true);
            }}
            name="EDIT_SUBMISSION_COMMENTS_BUTTON"
          />
        )}
      </Layout>
      <Gutter top="10px">
        {editComments ? (
          <Form
            name="EDIT_SUBMISSION_COMMENTS"
            schema={CandidateReputationCommentsSchema}
            initialValues={{ submissionComments: currentComments }}
            onSubmit={handleCommentsSubmit}
          >
            {({ fireSubmit }) => (
              <>
                <Textarea
                  name="submissionComments"
                  id={candidate.id}
                  placeholder="Additional details..."
                  full
                />
                <Gutter bottom="24px" />
                <FluidButtonLayout>
                  <Button
                    name="CANCEL_SUBMISSION_COMMENTS"
                    onClick={() => {
                      setEditComments(false);
                    }}
                    variation="outline"
                  >
                    Cancel
                  </Button>
                  <Button
                    name="SAVE_SUBMISSION_COMMENTS"
                    type="submit"
                    onClick={fireSubmit}
                  >
                    Save
                  </Button>
                </FluidButtonLayout>
              </>
            )}
          </Form>
        ) : (
          commentsDisplay
        )}
      </Gutter>
      <Gutter bottom="24px" />
    </>
  );
};
